'use client'

import { useCallback, useRef, type FC, type PropsWithChildren } from 'react'

import { AudioContext, type AudioRegistration, type RegisteredPlayer } from './audio-context'
import { safePlay } from './safe-play'

interface AudioCleanup {
  update: (event: Event) => void
  onEnded: () => void
}

const CLEANUP_KEY = Symbol('audioCleanup')

export const AudioProvider: FC<PropsWithChildren> = ({ children }) => {
  const players = useRef<Map<string, RegisteredPlayer>>(new Map())
  const keyboardAttached = useRef(false)
  const requested = useRef<{ id: string; isLoading: boolean } | null>(null)

  const getStatus = ({ id, audio }: RegisteredPlayer) => {
    const { currentTime, duration } = audio
    const validDuration = Number.isFinite(duration) && duration > 0
    let progress = validDuration ? currentTime / duration : 0
    if (progress >= 1) progress = 0
    const isLoading = requested.current?.id === id && requested.current.isLoading
    return {
      isPlaying: !audio.paused && !isLoading,
      isLoading,
      currentTime,
      duration: validDuration ? duration : 0,
      progress,
    }
  }

  const notifyUpdate = (player: RegisteredPlayer) => {
    player.onUpdate(getStatus(player))
  }

  const pause = useCallback(() => {
    requested.current = null
    players.current.forEach((player) => {
      player.audio.pause()
      notifyUpdate(player)
    })
  }, [])

  const toggle = useCallback((id: string) => {
    const current = players.current.get(id)
    if (!current) return

    if (current.audio.paused && requested.current?.id !== id) {
      // Pause all others
      players.current.forEach((player) => {
        if (player.id !== id) {
          player.audio.pause()
          notifyUpdate(player)
        }
      })

      // Group sync: set currentTime to max of group
      if (current.group) {
        let maxTime = current.audio.currentTime
        players.current.forEach((player) => {
          if (player.group === current.group && player.audio.currentTime > maxTime) {
            maxTime = player.audio.currentTime
          }
        })
        if (isNaN(current.audio.duration) || maxTime < current.audio.duration) {
          current.audio.currentTime = maxTime
        } else {
          current.audio.currentTime = 0
        }
      }

      const request = { id, isLoading: true }
      requested.current = request
      void safePlay(current.audio, (error) => {
        if (requested.current === request) {
          requested.current = null
          current.onPlayError?.(error)
          notifyUpdate(current)
        }
      }).then(() => {
        // A slow play request must not restart a replaced or unmounted clip.
        if (requested.current?.id !== id) {
          current.audio.pause()
        }
        if (requested.current === request) {
          request.isLoading = false
          notifyUpdate(current)
        }
      })
      notifyUpdate(current)
    } else {
      requested.current = null
      current.audio.pause()
      notifyUpdate(current)
    }
  }, [])

  const handleKeydown = useRef((e: KeyboardEvent) => {
    // Editing a field or moving through radio choices must not seek the audio.
    if (
      e.target instanceof Element &&
      e.target.closest('input, textarea, select, [contenteditable="true"], [role="radio"], [role="slider"]')
    ) {
      return
    }
    let active: RegisteredPlayer | undefined
    players.current.forEach((player) => {
      if (!player.audio.paused) active = player
    })
    if (!active) return

    const { audio } = active
    let newTime: number | undefined
    if (e.key === 'ArrowLeft') {
      newTime = Math.max(audio.currentTime - 10, 0)
    } else if (e.key === 'ArrowRight') {
      newTime = audio.currentTime + 10
      if (newTime >= audio.duration) newTime = 0
    } else if (e.key === 'ArrowUp') {
      newTime = 0
    }
    if (newTime !== undefined) {
      audio.currentTime = newTime
      notifyUpdate(active)
    }
  })

  const register = useCallback(
    ({ id, audio, group, onUpdate, onPlayError }: AudioRegistration): RegisteredPlayer => {
      const player: RegisteredPlayer = {
        id,
        group,
        audio,
        onUpdate,
        onPlayError,
        toggle: () => toggle(id),
      }

      // Event listeners
      const update = (event: Event) => {
        if ((event.type === 'error' || event.type === 'pause') && requested.current?.id === id) {
          requested.current = null
        }
        if (requested.current?.id === id) {
          if (event.type === 'playing') {
            requested.current.isLoading = false
          } else if (event.type === 'waiting' && !audio.paused) {
            requested.current.isLoading = true
          }
        }
        notifyUpdate(player)
      }
      const onEnded = () => {
        pause()
        notifyUpdate(player)
      }
      audio.addEventListener('loadedmetadata', update)
      audio.addEventListener('durationchange', update)
      audio.addEventListener('timeupdate', update)
      audio.addEventListener('play', update)
      audio.addEventListener('playing', update)
      audio.addEventListener('waiting', update)
      audio.addEventListener('pause', update)
      audio.addEventListener('error', update)
      audio.addEventListener('ended', onEnded)

      // Store cleanup refs on the element for deregister
      ;(audio as HTMLAudioElement & { [CLEANUP_KEY]?: AudioCleanup })[CLEANUP_KEY] = { update, onEnded }

      if (!keyboardAttached.current) {
        window.addEventListener('keydown', handleKeydown.current)
        keyboardAttached.current = true
      }

      players.current.set(id, player)
      return player
    },
    [toggle, pause]
  )

  const deregister = useCallback((id: string) => {
    const player = players.current.get(id)
    if (!player) return

    if (requested.current?.id === id) {
      requested.current = null
    }
    player.audio.pause()
    const cleanup = (player.audio as HTMLAudioElement & { [CLEANUP_KEY]?: AudioCleanup })[CLEANUP_KEY]
    if (cleanup) {
      player.audio.removeEventListener('loadedmetadata', cleanup.update)
      player.audio.removeEventListener('durationchange', cleanup.update)
      player.audio.removeEventListener('timeupdate', cleanup.update)
      player.audio.removeEventListener('play', cleanup.update)
      player.audio.removeEventListener('playing', cleanup.update)
      player.audio.removeEventListener('waiting', cleanup.update)
      player.audio.removeEventListener('pause', cleanup.update)
      player.audio.removeEventListener('error', cleanup.update)
      player.audio.removeEventListener('ended', cleanup.onEnded)
      delete (player.audio as HTMLAudioElement & { [CLEANUP_KEY]?: AudioCleanup })[CLEANUP_KEY]
    }
    players.current.delete(id)
    if (players.current.size === 0 && keyboardAttached.current) {
      window.removeEventListener('keydown', handleKeydown.current)
      keyboardAttached.current = false
    }
  }, [])

  return <AudioContext.Provider value={{ register, deregister }}>{children}</AudioContext.Provider>
}
